import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';

/**
 * Shared scroll-reveal (spec 010 §6): opacity + 8px translate, 400ms, once.
 * API matches the per-section local copies: `{ ref, isVisible }`, `threshold` first arg.
 * Under reduced motion (or without IntersectionObserver) `isVisible` is true immediately.
 *
 * Use `revealStyle(isVisible, delayMs)` (or the `revealClass`) so every consumer shares the same motion.
 */
export function useScrollReveal(threshold = 0.05) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = usePrefersReducedMotion();
  const noObserver = typeof IntersectionObserver === 'undefined';
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    if (reduced || noObserver) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin: '0px 0px 100px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, reduced, noObserver]);

  return { ref, isVisible: seen || reduced || noObserver };
}

/** Inline style for a revealed element. No motion under reduced motion (isVisible starts true). */
export function revealStyle(isVisible: boolean, delayMs = 0): CSSProperties {
  return {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(8px)',
    transition: `opacity 400ms ease-out ${delayMs}ms, transform 400ms ease-out ${delayMs}ms`,
  };
}
